import cx from 'classnames';
import {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectClassifiedCategory} from '~/common/fabric/reducers/eviteEvent/selectors';
import {
  selectHasMultipleRegistries,
  selectHasRegistries,
} from '~/common/fabric/reducers/registry/selectors';
import {FUNERAL_CATEGORY} from '~/common/utils/event_constants';
import {doesMatchEventCategory} from '~/common/utils/event_utils';
import {RSVPModalProps} from '~/ViewInvitation/common/RSVPModalTestAB';
import {Nift} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/Nift/Nift';
import {callNift} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/Nift/niftApi';
import {
  selectEventTemplateName,
  selectIsFuneral,
  selectRoktDisabled,
  selectShowGifting,
} from '~/ViewInvitation/selectors/eviteEvent';
import {
  selectEmailMd5,
  selectEmailSha256,
  selectHasRsvpd,
  selectMarketingContext,
  selectNotifications,
} from '~/ViewInvitation/selectors/rsvp';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {getUserData} from '~library/utils/auth/utils';
import {CecPreferences, ChuckECheese} from './steps/ChuckECheese/ChuckECheese';
import {CollectInfoVariant} from './steps/CollectInfoVariant/CollectInfoVariant';
import {Animation} from './steps/components/ConfettiAnimation';
import {GiftIdeas} from './steps/GiftIdeas/GiftIdeas';
import {RegistryWishlist} from './steps/RegistryWishlist/RegistryWishlist';
import {RSVPConfirmed} from './steps/RSVPConfirmed/RSVPConfirmed';
import {SuccessRokt} from './steps/SuccessRokt/SuccessRokt';
import {useSuccessPageRokt} from './steps/SuccessRokt/useSuccessPageRokt';
import {Payload} from './types';

import './RSVPModalVariantAB.sass';

const dataLayerCache: Map<string, string> = new Map();

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: couldn't break into smaller components
export const RSVPModalVariantAB = ({open, status, onClose, onSubmitRSVP}: RSVPModalProps) => {
  const isMediumAndUpScreen = useIsMediumAndUp();
  const hasRegistries = useSelector(selectHasRegistries);
  const hasMultipleRegistries = useSelector(selectHasMultipleRegistries);
  const templateName = useSelector(selectEventTemplateName);
  const showGifting = useSelector(selectShowGifting);
  const isChuckECheese = templateName.includes('chuckecheese');
  const [step, setStep] = useState(0);
  const [stepsCounter, setStepsCounter] = useState(0);
  const {
    initRokt,
    hasOffers,
    initialized: roktInitialized,
    clearRoktInstance,
  } = useSuccessPageRokt();
  const [payload, setPayload] = useState({} as Payload);
  const hasRsvpd = useSelector(selectHasRsvpd);
  const notifications = useSelector(selectNotifications);
  const marketingContext = useSelector(selectMarketingContext);
  const hasRsvpdRef = useRef(hasRsvpd);
  const emailMd5 = useSelector(selectEmailMd5);
  const emailMd5Ref = useRef(emailMd5);
  const emailSha256 = useSelector(selectEmailSha256);
  const emailSha256Ref = useRef(emailSha256);
  const userData = getUserData();
  const searchParams = new URLSearchParams(window.location.search);
  const gid = searchParams.get('gid');
  const guestId = userData.guest_id || userData.guestId || gid || window.guest_id;
  const initRoktTimeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFuneralType = useSelector(selectIsFuneral);
  const classifiedCategory = useSelector(selectClassifiedCategory);
  const isFuneral = isFuneralType || doesMatchEventCategory(classifiedCategory, FUNERAL_CATEGORY);
  const [rsvpDone, setRsvpDone] = useState(false);
  const isRoktDisabledForEvent = useSelector(selectRoktDisabled);
  const [niftGiftAvailable, setNiftGiftAvailable] = useState(false);
  const [niftAffiliateUrl, setNiftAffiliateUrl] = useState('');

  const onClickCloseHandler = useCallback(async () => {
    await clearRoktInstance();
    setStep(0);
    onClose();

    if (rsvpDone && hasMultipleRegistries) {
      const anchorElement = document.querySelector('.submodule-registry');
      if (anchorElement) {
        anchorElement?.scrollIntoView({behavior: 'smooth'});
      }
    }
  }, [onClose, clearRoktInstance, hasMultipleRegistries, rsvpDone]);

  // This will inject the user ID, and notification options and into the RSVP payload
  const onSubmitHandler = useCallback(
    (info: Payload) => {
      // biome-ignore-start lint/style/useNamingConvention: API expects in snake_case
      let notificationOptions = notifications;

      if (!hasRsvpd) {
        notificationOptions = {
          email_new_comments: true,
          email_new_rsvp: false,
          push_new_comments: true,
          push_new_rsvp: true,
          notify_only_my_activity: true,
          notify_only_my_activity_push: true,
        };
      }

      setRsvpDone(true);

      return onSubmitRSVP(
        {
          ...info,
          guest_id: guestId,
          notification_options: notificationOptions,
          marketing_context: marketingContext,
        },
        true
      );
      // biome-ignore-end lint/style/useNamingConvention: API expects in snake_case
    },
    [guestId, hasRsvpd, notifications, onSubmitRSVP, marketingContext]
  );

  const saveRSVP = useCallback(
    async (info: Payload) => {
      setPayload(info);
      return onSubmitHandler(info);
    },
    [onSubmitHandler]
  );

  useEffect(() => {
    document.body.classList.toggle('no-scroll', open);

    return () => {
      document.body.classList.remove('no-scroll');
      dataLayerCache.clear();
    };
  }, [open]);

  const onGoForwardClick = useCallback(() => {
    const nextStep = step + 1;
    if (nextStep >= stepsCounter) {
      onClickCloseHandler();
    } else {
      setStep(nextStep);
    }
  }, [step, stepsCounter, onClickCloseHandler]);

  useEffect(() => {
    if (hasRsvpd && emailMd5 && emailSha256) {
      hasRsvpdRef.current = hasRsvpd;
      emailMd5Ref.current = emailMd5;
      emailSha256Ref.current = emailSha256;
    }
  }, [hasRsvpd, emailMd5, emailSha256]);

  useEffect(() => {
    return () => {
      if (initRoktTimeoutId.current) {
        clearTimeout(initRoktTimeoutId.current);
      }
    };
  }, []);

  const initRoktCb = useCallback(async () => {
    // Wait until hasRsvpdRef.current becomes true
    await new Promise<void>((resolve) => {
      const check = () => {
        if (hasRsvpdRef.current && emailMd5Ref.current && emailSha256Ref.current) {
          resolve();
        } else {
          initRoktTimeoutId.current = setTimeout(check, 100);
        }
      };
      check();
    });

    // Once hasRsvpdRef.current is true, initialize ROKT
    if (!roktInitialized) {
      await initRokt({
        emailMd5: emailMd5Ref.current,
        emailSha256: emailSha256Ref.current,
      });
    }
  }, [roktInitialized, initRokt]);

  // If ROKT is initialized & is first render of RSVP modal, go to next step
  useEffect(() => {
    if (step === 0 && roktInitialized) {
      onGoForwardClick();
    }
  }, [step, onGoForwardClick, roktInitialized]);

  const onCecSave = useCallback(
    async (newInfo: CecPreferences) => {
      await onSubmitHandler({...payload, ...newInfo});
      onGoForwardClick();
    },
    [onGoForwardClick, onSubmitHandler, payload]
  );

  const storeInfoHandler = useCallback(
    async (info: Payload) => {
      const {available, url} = await callNift({email: info.email || '', referralCode: 'EMQTEST34'});

      setNiftGiftAvailable(available);
      setNiftAffiliateUrl(url);

      try {
        await saveRSVP(info);
        if (isRoktDisabledForEvent) {
          onGoForwardClick();
        } else {
          await initRoktCb();
        }
      } catch (error) {
        evite.error(error);
      }
    },
    [saveRSVP, initRoktCb, isRoktDisabledForEvent, onGoForwardClick]
  );

  const steps = useMemo(() => {
    const stepsList: Array<{
      key: string;
      trackingData: string;
      component: ReactElement;
      shouldPlayAnimation: boolean;
      mobileAutoHeight: boolean;
    }> = [];
    const hasRoktOffers = roktInitialized && hasOffers;

    stepsList.push({
      key: 'collect-info-variant',
      trackingData: 'CollectInfo-modal-shown',
      component: (
        <CollectInfoVariant
          status={status}
          onCtaClick={storeInfoHandler}
          onClickClose={onClickCloseHandler}
        />
      ),
      shouldPlayAnimation: false,
      mobileAutoHeight: false,
    });

    if (isChuckECheese) {
      stepsList.push({
        key: 'chuck-e-cheese',
        trackingData: 'ChuckECheese-modal-shown',
        component: <ChuckECheese onCtaClick={onCecSave} onCloseClick={onGoForwardClick} />,
        shouldPlayAnimation: false,
        mobileAutoHeight: false,
      });
    }

    if (isFuneral) {
      if (hasRoktOffers) {
        stepsList.push({
          key: 'success-rokt-funeral',
          trackingData: 'Rokt-modal-shown',
          component: (
            <SuccessRokt onPlacementClosed={onClickCloseHandler} onClose={onClickCloseHandler} />
          ),
          shouldPlayAnimation: false,
          mobileAutoHeight: true,
        });

        setStepsCounter(stepsList.length);
        return stepsList;
      }

      if (niftGiftAvailable) {
        stepsList.push({
          key: 'nift-success',
          trackingData: 'Nift-modal-shown',
          component: <Nift url={niftAffiliateUrl} onClose={onClickCloseHandler} />,
          shouldPlayAnimation: false,
          mobileAutoHeight: true,
        });

        setStepsCounter(stepsList.length);
        return stepsList;
      }

      stepsList.push({
        key: 'rsvp-confirmed-funeral',
        trackingData: 'RSVPConfirmed-modal-shown',
        component: (
          <RSVPConfirmed
            imageSrc="https://g0.evitecdn.com/invitation/dove_envelope_flower.gif"
            onCloseClick={onClickCloseHandler}
          />
        ),
        shouldPlayAnimation: false,
        mobileAutoHeight: false,
      });

      setStepsCounter(stepsList.length);
      return stepsList;
    }

    if (hasRegistries) {
      if (hasRoktOffers) {
        stepsList.push({
          trackingData: 'Registry-modal-shown',
          key: 'registry-wishlist',
          component: <RegistryWishlist onCloseClick={onGoForwardClick} />,
          shouldPlayAnimation: false,
          mobileAutoHeight: true,
        });
        stepsList.push({
          key: 'success-rokt-registry',
          trackingData: 'Rokt-modal-shown',
          component: (
            <SuccessRokt onPlacementClosed={onClickCloseHandler} onClose={onClickCloseHandler} />
          ),
          shouldPlayAnimation: true,
          mobileAutoHeight: true,
        });

        setStepsCounter(stepsList.length);
        return stepsList;
      }

      if (niftGiftAvailable) {
        stepsList.push({
          trackingData: 'Registry-modal-shown',
          key: 'registry-wishlist',
          component: <RegistryWishlist onCloseClick={onGoForwardClick} />,
          shouldPlayAnimation: false,
          mobileAutoHeight: true,
        });

        stepsList.push({
          key: 'nift-success',
          trackingData: 'Nift-modal-shown',
          component: <Nift url={niftAffiliateUrl} onClose={onClickCloseHandler} />,
          shouldPlayAnimation: true,
          mobileAutoHeight: true,
        });

        setStepsCounter(stepsList.length);
        return stepsList;
      }

      stepsList.push({
        key: 'registry-wishlist-no-rokt',
        trackingData: 'Registry-modal-shown',
        component: (
          <RegistryWishlist onCloseClick={onGoForwardClick} altTitle="Your RSVP is confirmed" />
        ),
        shouldPlayAnimation: true,
        mobileAutoHeight: true,
      });

      setStepsCounter(stepsList.length);
      return stepsList;
    }

    if (hasRoktOffers) {
      if (showGifting) {
        stepsList.push({
          key: 'success-rokt-gifting',
          trackingData: 'Rokt-modal-shown',
          component: (
            <SuccessRokt
              onPlacementClosed={onGoForwardClick}
              onClose={onGoForwardClick}
              showFooter
            />
          ),
          shouldPlayAnimation: true,
          mobileAutoHeight: false,
        });

        stepsList.push({
          key: 'gift-ideas',
          trackingData: 'Gifting-modal-shown',
          component: <GiftIdeas onCloseClick={onClickCloseHandler} />,
          shouldPlayAnimation: false,
          mobileAutoHeight: true,
        });

        setStepsCounter(stepsList.length);
        return stepsList;
      }

      stepsList.push({
        key: 'success-rokt-no-gifting',
        trackingData: 'Rokt-modal-shown',
        component: (
          <SuccessRokt onPlacementClosed={onClickCloseHandler} onClose={onClickCloseHandler} />
        ),
        shouldPlayAnimation: true,
        mobileAutoHeight: true,
      });

      setStepsCounter(stepsList.length);
      return stepsList;
    }

    if (showGifting) {
      if (niftGiftAvailable) {
        stepsList.push({
          key: 'nift-success',
          trackingData: 'Nift-modal-shown',
          component: <Nift url={niftAffiliateUrl} onClose={onGoForwardClick} />,
          shouldPlayAnimation: true,
          mobileAutoHeight: true,
        });

        stepsList.push({
          key: 'gift-ideas-nift',
          trackingData: 'Gifting-modal-shown',
          component: <GiftIdeas onCloseClick={onClickCloseHandler} />,
          shouldPlayAnimation: false,
          mobileAutoHeight: true,
        });

        setStepsCounter(stepsList.length);
        return stepsList;
      }

      stepsList.push({
        key: 'gift-ideas-no-rokt',
        trackingData: 'Gifting-modal-shown',
        component: <GiftIdeas variant onCloseClick={onClickCloseHandler} />,
        shouldPlayAnimation: true,
        mobileAutoHeight: true,
      });
    } else {
      if (niftGiftAvailable) {
        stepsList.push({
          key: 'nift-success',
          trackingData: 'Nift-modal-shown',
          component: <Nift url={niftAffiliateUrl} onClose={onClickCloseHandler} />,
          shouldPlayAnimation: true,
          mobileAutoHeight: true,
        });

        setStepsCounter(stepsList.length);
        return stepsList;
      }

      stepsList.push({
        key: 'rsvp-confirmed',
        trackingData: 'RSVPConfirmed-modal-shown',
        component: (
          <RSVPConfirmed
            imageSrc="https://g0.evitecdn.com/invitation/confetti-hands.png"
            onCloseClick={onClickCloseHandler}
          />
        ),
        shouldPlayAnimation: true,
        mobileAutoHeight: false,
      });
    }

    setStepsCounter(stepsList.length);

    return stepsList;
  }, [
    niftGiftAvailable,
    niftAffiliateUrl,
    status,
    storeInfoHandler,
    onClickCloseHandler,
    isChuckECheese,
    roktInitialized,
    hasRegistries,
    isFuneral,
    showGifting,
    hasOffers,
    onCecSave,
    onGoForwardClick,
  ]);

  useEffect(() => {
    const currentTrackingData = steps[step].trackingData;
    if (dataLayerCache.get(currentTrackingData) !== currentTrackingData) {
      dataLayerCache.set(currentTrackingData, currentTrackingData);
      window.dataLayer.push({event: currentTrackingData});
    }
  }, [step, steps]);

  const renderModalContent = useCallback(
    () =>
      steps.map((stepItem, index) => {
        const stepIsVisible = index === step;
        return (
          <div
            key={stepItem.key || `step-${index}`}
            className={cx('RSVP-modal__content', {
              'RSVP-modal__content--active': stepIsVisible,
              'RSVP-modal__content--mobile-auto-height': stepIsVisible && stepItem.mobileAutoHeight,
            })}
          >
            {stepItem.component}
            <Animation status={status} play={stepIsVisible && stepItem.shouldPlayAnimation} />
          </div>
        );
      }),
    [status, step, steps]
  );

  if (isMediumAndUpScreen) {
    return (
      <ResponsiveModal
        isModal
        isOpen={open}
        onClose={() => null}
        overlayClassName="RSVP-modal"
        showHeader={false}
      >
        {renderModalContent()}
      </ResponsiveModal>
    );
  }

  return (
    <BottomSheet
      in={open}
      className="custom-bottom-sheet__overlay__content"
      content={renderModalContent()}
      aboveOtherModals
    />
  );
};
